/**
 * Pay-as-you-go minute packs (TOS-52): the pure, dependency-free half.
 *
 * Catalog + the arithmetic the wallet relies on, with no db/env imports so it
 * is unit-testable and safe to import from client components (pricing UI).
 * The db side (grant / consume / claw back) lives in ./payg.ts.
 *
 * Prices mirror the one-time Polar products created 2026-07-14
 * (docs/payments/POLAR_SETUP_PRODUCTS_AND_WEBHOOKS.md §PAYG). Polar is the
 * source of truth for the amount charged; these numbers are display copy and
 * must be kept in sync with the dashboard.
 */

export type PaygPackSize = '120' | '300' | '600';

export interface PaygPack {
  size: PaygPackSize;
  /** Polar product metadata `product_code`; also stored on payg_grants. */
  code: string;
  name: string;
  minutes: number;
  priceUsd: number;
}

export const PAYG_PACKS: readonly PaygPack[] = [
  { size: '120', code: 'wisprs_payg_120', name: 'Episode Pack', minutes: 120, priceUsd: 9 },
  { size: '300', code: 'wisprs_payg_300', name: 'Creator Pack', minutes: 300, priceUsd: 19 },
  { size: '600', code: 'wisprs_payg_600', name: 'Season Pack', minutes: 600, priceUsd: 35 },
] as const;

/** Minutes stay usable for this long after the most recent purchase. */
export const PAYG_EXPIRY_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;

export function paygPackBySize(size: string | null | undefined): PaygPack | null {
  return PAYG_PACKS.find((p) => p.size === size) ?? null;
}

/**
 * The cheapest single pack that covers `neededMinutes` of paid overshoot, for
 * the paywall's "buy minutes for this file" offer. Null when nothing is needed
 * or no single pack is big enough (the paywall then offers the plan alone).
 */
export function paygPackCovering(neededMinutes: number): PaygPack | null {
  if (!Number.isFinite(neededMinutes) || neededMinutes <= 0) return null;

  return PAYG_PACKS.find((p) => p.minutes >= neededMinutes) ?? null;
}

export function isPaygBalanceExpired(expiresAt: Date | null, now: Date = new Date()): boolean {
  return expiresAt !== null && expiresAt.getTime() <= now.getTime();
}

/**
 * The wallet after crediting a pack bought at `now`.
 *
 * One expiry column covers every stacked pack, so a new purchase pushes the
 * whole balance out to now + 12 months (never pulls it in). A balance that had
 * already expired is dead money we don't resurrect: it resets to just the new
 * pack's minutes.
 */
export function paygBalanceAfterGrant(
  current: { available: number; expiresAt: Date | null },
  packMinutes: number,
  now: Date = new Date(),
): { available: number; expiresAt: Date } {
  const newExpiry = new Date(now.getTime() + PAYG_EXPIRY_DAYS * DAY_MS);
  const expired = isPaygBalanceExpired(current.expiresAt, now);
  const carried = expired ? 0 : Math.max(0, current.available);

  const expiresAt =
    !expired && current.expiresAt && current.expiresAt.getTime() > newExpiry.getTime()
      ? current.expiresAt
      : newExpiry;

  return { available: carried + packMinutes, expiresAt };
}

/**
 * Minutes to take back when a pack order is refunded: the pack's minutes, but
 * never more than is still unspent (minutes already used on transcriptions
 * stay used — the refund itself is Polar's business, not the wallet's).
 */
export function paygClawBackMinutes(packMinutes: number, available: number): number {
  return Math.max(0, Math.min(packMinutes, available));
}
